// Browser-side first-touch capture. Runs on mount for both entry points
// (/ and /app/connect) and writes ONCE per tab session — the first URL a
// visitor arrives on is the one that gets the credit, so a later
// navigation that happens to carry different UTMs must not overwrite it.
//
// Field list + encoding live in @shared/leadgen/attribution; this file
// only owns the sessionStorage side.

import {
  ATTRIBUTION_PARAM_KEYS,
  MAX_ATTRIBUTION_VALUE_LENGTH,
  type FirstTouchAttribution,
} from "@shared/leadgen/attribution";

const FIRST_TOUCH_KEY = "ts.attribution.first_touch";

/** Capture the current URL's attribution into sessionStorage if — and
 *  only if — nothing is stored yet. Returns the stored blob (existing or
 *  freshly written), or null when storage is unavailable.
 *
 *  Never throws: Safari private mode and SSR both make sessionStorage
 *  access fatal, and a measurement helper must not be able to break the
 *  page it's measuring. */
export function captureFirstTouch(): FirstTouchAttribution | null {
  if (typeof window === "undefined") return null;
  try {
    const existing = readFirstTouch();
    if (existing) return existing;

    const params = new URLSearchParams(window.location.search);
    const attribution: FirstTouchAttribution = { captured_at: Date.now() };
    for (const key of ATTRIBUTION_PARAM_KEYS) {
      // landing_path + referrer aren't query params — they're read off
      // the document below.
      const value = params.get(key);
      if (value) attribution[key] = value.slice(0, MAX_ATTRIBUTION_VALUE_LENGTH);
    }
    attribution.landing_path = window.location.pathname.slice(0, MAX_ATTRIBUTION_VALUE_LENGTH);
    const referrer = typeof document === "undefined" ? "" : document.referrer;
    if (referrer) attribution.referrer = referrer.slice(0, MAX_ATTRIBUTION_VALUE_LENGTH);

    window.sessionStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(attribution));
    return attribution;
  } catch {
    return null;
  }
}

/** Read the stored first touch, or null if absent / unparseable /
 *  storage unavailable. Never throws. */
export function readFirstTouch(): FirstTouchAttribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(FIRST_TOUCH_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const candidate = parsed as FirstTouchAttribution;
    if (typeof candidate.captured_at !== "number") return null;
    return candidate;
  } catch {
    return null;
  }
}
